import { format, parseISO } from 'date-fns'
import { RoutesManager } from 'utils/routing/routesManager'
import React, { useEffect, useContext, useState, useMemo, useCallback, Fragment } from 'react'
import { Box, styled, Popover, Divider } from '@mui/material'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  DropListButton,
  ListLayout,
  TextInput,
  DateRangePicker,
  DatePickersActionBar,
  DatePickersActionBarProps,
  TabGroup,
  Tab,
  ListItemText,
  ActionSheetItem,
  Button,
  Icon,
  IconDictionary,
  Spinner,
} from '@ketch-com/deck'
import { SidebarSection } from 'components/appLayout/appNavigation/components/navigationSidebar/utils'
import { AppNavigationContext } from 'components/appLayout/appNavigation/context/AppNavigationContext'
import { NavigationBreadCrumbs } from 'components/appLayout/appNavigation/breadcrumbs/NavigationBreadCrumbs'
import dayjs, { Dayjs } from 'dayjs'
import { DateRange } from '@mui/x-date-pickers-pro'
import { useDataSentryContext } from 'pages/dataSentry/context/DataSentryContext'
import { PropertyOption, TimeRangeOption } from '../utils/types'
import { isProd } from 'utils/environment'
import pluralize from 'pluralize'
import { ScanHistoryModal } from 'pages/dataSentry/components/ScanHistoryModal'
import { ScanUrlsModal } from 'pages/dataSentry/components/ScanUrlsModal'

const FilterContainer = styled(Box)({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  padding: '24px 0px 0px 0px',
})

const ContentContainer = styled(Box)({
  maxWidth: '1280px',
  width: '100%',
})

const tabs = [
  { label: 'Overview', path: RoutesManager.dataSentry.overview.getURL() },
  { label: 'Systems', path: RoutesManager.dataSentry.systems.getURL() },
  { label: 'Data', path: RoutesManager.dataSentry.data.getURL() },
  { label: 'Trackers', path: RoutesManager.dataSentry.trackers.getURL() },
]

export const DataSentryDashboard: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0)
  const [isScanHistoryModalOpen, setIsScanHistoryModalOpen] = useState(false)
  const [isScanUrlsModalOpen, setIsScanUrlsModalOpen] = useState(false)
  const {
    selectedProperty,
    setSelectedProperty,
    selectedTimeRange,
    setSelectedTimeRange,
    scanHistory,
    scannedUrls,
    isLoadingScanHistory,
    properties,
    isLoadingProperties,
    hasPropertyAndScanTimeRange,
  } = useDataSentryContext()

  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { setSidebarOpenSection } = useContext(AppNavigationContext)
  const breadcrumbs = [{ title: 'Data Sentry' }]
  const [datePickerAnchorEl, setDatePickerAnchorEl] = useState<HTMLElement | null>(null)
  const [tempDateRange, setTempDateRange] = useState<DateRange<Dayjs>>([null, null])

  const scannedDates: TimeRangeOption[] = useMemo(() => {
    if ((isLoadingScanHistory && !scanHistory) || scanHistory?.scans?.length === 0) return []

    return (
      (scanHistory?.scans?.map((scan, i) => ({
        id: scan.runId || `scan-${i}`,
        name: format(parseISO(scan?.runDate || ''), 'MMM d, yyyy'),
        startDate: scan.startDate,
        endDate: scan.endDate,
      })) as TimeRangeOption[]) || []
    )
  }, [isLoadingScanHistory, scanHistory])

  useMemo(() => {
    if (!isProd() && scannedDates.length) {
      scannedDates.push({
        id: 'custom',
        name: 'Custom',
        startDate: '',
        endDate: '',
      })
    }
  }, [scannedDates])

  useEffect(() => {
    setSidebarOpenSection(SidebarSection.DataSentry)
  }, [pathname, setSidebarOpenSection])

  useEffect(() => {
    setSelectedProperty(properties[0] || null)
  }, [properties, setSelectedProperty])

  useEffect(() => {
    setSelectedTimeRange(scannedDates[0])
  }, [scannedDates, setSelectedTimeRange])

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
    navigate(tabs[newValue].path)
  }

  const handleDatePickerClose = useCallback(() => {
    setDatePickerAnchorEl(null)
  }, [])

  const handleDatePickerClear = () => {
    setTempDateRange([null, null])
  }

  const handleDatePickerApply = useCallback(() => {
    const [start, end] = tempDateRange
    if (start && end && start.isValid() && end.isValid()) {
      const formattedStartDate = start.format('YYYY-MM-DD')
      const formattedEndDate = end.format('YYYY-MM-DD')
      const displayStartDate = start.format('MMM D')
      const displayEndDate = end.format('MMM D, YYYY')
      const displayRange = `${displayStartDate} - ${displayEndDate}`

      setSelectedTimeRange({
        id: 'custom',
        name: displayRange,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      })
      handleDatePickerClose()
    }
  }, [tempDateRange, setSelectedTimeRange, handleDatePickerClose])

  const isDatePickerOpen = Boolean(datePickerAnchorEl)

  const handleTimeRangeChange = useCallback(
    (e: React.SyntheticEvent, option: TimeRangeOption | null) => {
      if (option?.id === 'custom') {
        setTempDateRange([
          selectedTimeRange?.id === 'custom' && selectedTimeRange.startDate ? dayjs(selectedTimeRange.startDate) : null,
          selectedTimeRange?.id === 'custom' && selectedTimeRange.endDate ? dayjs(selectedTimeRange.endDate) : null,
        ])
        setDatePickerAnchorEl(e.currentTarget as HTMLButtonElement)
      } else if (option) {
        setSelectedTimeRange(option)
        handleDatePickerClose()
      }
    },
    [
      selectedTimeRange?.id,
      selectedTimeRange?.startDate,
      selectedTimeRange?.endDate,
      setSelectedTimeRange,
      handleDatePickerClose,
    ],
  )

  const renderTimeRangeInput = useCallback(
    (params: any) => {
      if (selectedTimeRange?.id === 'custom') {
        return (
          <TextInput
            {...params}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
              setDatePickerAnchorEl(e.currentTarget as HTMLDivElement)
              setTempDateRange([
                selectedTimeRange.startDate ? dayjs(selectedTimeRange.startDate) : null,
                selectedTimeRange.endDate ? dayjs(selectedTimeRange.endDate) : null,
              ])
            }}
            InputProps={{
              ...params.InputProps,
              readOnly: true,
              style: { cursor: 'pointer' },
            }}
            placeholder={'Select Range'}
            variant="outlined"
          />
        )
      }

      return (
        <TextInput
          {...params}
          InputProps={{
            ...params.InputProps,
          }}
          placeholder={'Select Range'}
          variant="outlined"
        />
      )
    },
    [selectedTimeRange],
  )

  const renderPropertyOption = useCallback(
    (props: any, property: PropertyOption | null, state: { selected: boolean; index: number }) => {
      if (!property?.name && !property?.code) return null
      return (
        <Fragment key={property?.code}>
          {state.index > 0 && <ActionSheetItem divider />}
          <ActionSheetItem selected={state.selected} {...props}>
            <ListItemText selected={state.selected} primary={property?.name} secondary={property?.code} />
          </ActionSheetItem>
        </Fragment>
      )
    },
    [],
  )

  return (
    <ListLayout headerTitle="Data Sentry">
      <ContentContainer>
        <NavigationBreadCrumbs type="light" items={breadcrumbs} />
        <TabGroup tabItems={tabs} value={selectedTab} onChange={handleTabChange}>
          {tabs.map(tab => (
            <Tab key={tab.label} label={tab.label} />
          ))}
        </TabGroup>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <FilterContainer>
            <DropListButton
              options={properties}
              size="small"
              width="270px"
              loading={isLoadingProperties}
              value={selectedProperty ?? ({} as PropertyOption)}
              clearOnBlur
              noOptionsText="No Properties"
              onChange={(e, option) => {
                setSelectedProperty(option)
              }}
              renderInput={params => (
                <TextInput
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                  }}
                  placeholder="All Properties"
                  variant="outlined"
                />
              )}
              renderOption={renderPropertyOption}
              getOptionLabel={option => option?.name || ''}
              isOptionEqualToValue={(option, value) => option?.ID === value?.ID}
              disableClearable
            />

            <DropListButton
              disabled={isLoadingScanHistory}
              size="small"
              options={scannedDates}
              value={selectedTimeRange ?? ({} as TimeRangeOption)}
              clearOnBlur
              noOptionsText="No Time Ranges"
              onChange={handleTimeRangeChange}
              renderInput={renderTimeRangeInput}
              renderOption={(props, option: TimeRangeOption | null, state: { selected: boolean; index: number }) => (
                <Fragment key={option?.id}>
                  {state.index > 0 && <ActionSheetItem divider />}
                  <ActionSheetItem selected={state.selected} {...props}>
                    <ListItemText selected={state.selected} primary={option?.name} />
                  </ActionSheetItem>
                </Fragment>
              )}
              getOptionLabel={(option: TimeRangeOption | null) => option?.name || ''}
              isOptionEqualToValue={(option: TimeRangeOption | null, value: TimeRangeOption | null) =>
                option?.id === value?.id
              }
              disableClearable
            />
            <Popover
              open={isDatePickerOpen}
              anchorEl={datePickerAnchorEl}
              onClose={handleDatePickerClose}
              anchorReference="anchorPosition"
              anchorPosition={{ top: window.innerHeight / 4, left: window.innerWidth / 4 }}
              marginThreshold={16}
            >
              <Box p={2}>
                <DateRangePicker
                  value={tempDateRange}
                  onChange={setTempDateRange}
                  disableFuture
                  shouldDisableDate={date => {
                    const [start, end] = tempDateRange

                    if (start && !end) {
                      return date.isAfter(start.add(30, 'day'))
                    }

                    if (!start && end) {
                      return date.isBefore(end.subtract(30, 'day'))
                    }

                    return false
                  }}
                  slots={{
                    actionBar: (props: DatePickersActionBarProps) => (
                      <DatePickersActionBar
                        {...props}
                        handleOnClear={handleDatePickerClear}
                        onAccept={handleDatePickerApply}
                      ></DatePickersActionBar>
                    ),
                  }}
                />
              </Box>
            </Popover>
          </FilterContainer>
          <Box display="flex" alignItems="bottom" gap={3} pt={3}>
            {scanHistory?.scans?.length && scanHistory?.scans?.[0]?.runDate ? (
              <Button
                startIcon={<Icon name={IconDictionary.FSearchItems} />}
                variant="link"
                color="secondary"
                size="medium"
                onClick={() => setIsScanHistoryModalOpen(true)}
              >
                Last Scan: {format(parseISO(scanHistory?.scans[0]?.runDate), 'MMM d, yyyy')}
              </Button>
            ) : null}
            {scannedUrls?.total ? (
              <Button
                startIcon={<Icon name={IconDictionary.FLink} />}
                variant="link"
                color="secondary"
                onClick={() => setIsScanUrlsModalOpen(true)}
                size="medium"
              >
                {scannedUrls?.total} {pluralize('URL', scannedUrls?.total)} Scanned
              </Button>
            ) : null}
          </Box>
        </Box>
        <Divider sx={{ mt: 2, mb: 3 }} />
        <Box sx={{ mt: 3 }}>
          {hasPropertyAndScanTimeRange ? (
            <Outlet />
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" height={400}>
              <Spinner size="32px" thickness={2.5} />
            </Box>
          )}
        </Box>
      </ContentContainer>
      {isScanHistoryModalOpen ? (
        <ScanHistoryModal isOpen={isScanHistoryModalOpen} onClose={() => setIsScanHistoryModalOpen(false)} />
      ) : null}

      {isScanUrlsModalOpen ? (
        <ScanUrlsModal isOpen={isScanUrlsModalOpen} onClose={() => setIsScanUrlsModalOpen(false)} />
      ) : null}
    </ListLayout>
  )
}

export default DataSentryDashboard
